import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { catchError, Observable, of, take } from 'rxjs';
import { GeneralService } from './general.service';
import { InputsGeneralForm } from '../models/pages-model/form-general.model';
import { environment } from '../../../environments/environment';
import { FormControl } from '@angular/forms';

export interface Colony {
  id: number;
  description: string;
  value: string;
}

export interface AddressInfo {
  delegacion: string;
  ciudad: string;
  estado: string;
  colonies: Colony[];
}

@Injectable({
  providedIn: 'root',
})
export class InputFuntionService {
  public URL_API = environment.apiURLNew;
  public emitFuntionInput = new EventEmitter<{ action: string; body: any }>();

  constructor(
    public dialog: MatDialog,
    private http: HttpClient,
    private generalService: GeneralService,
  ) {}

  public colectionFuntions(
    input: InputsGeneralForm,
    fields: InputsGeneralForm[],
    quotationId: number,
    rumFuntions: boolean, // Indica indica si el campo debe marcarse como modificado (dirty), En Page General: startFuntion => true, rumFuntions => false
  ) {
    //***//
    // NOTA Existen mas funciones propias de el formulario
    // resetSuscrip => Resetea las subscripciones de los formularios
    // resetSuscripAutoSave => Resetea las subscripciones de los formularios y guarda el campo automaticamente al detectar cambios
    //***//

    // PostalCode-C=>26-D=>29-E=>27-Col=>25
    this.funtionCopiPostalCode(input, fields, rumFuntions);

    // CopySolicitudRFC-FULL por tipo de persona
    this.copySolicitudFull(input, quotationId);

    // Sum-26-29-27-=>25
    this.addValues(input, fields, rumFuntions);

    // Copia valor de campo (Fisica) P=>2 o (Moral) P=>1
    this.copiValue(input, fields, rumFuntions);
  }

  // suma-26-29-27-=>25 suma los valores de los campos n (26-29-27) y lo asigna al (=>) 25
  public addValues(input: InputsGeneralForm, fields: InputsGeneralForm[], rumFuntions: boolean) {
    if (
      typeof input.functionEvent?.event?.function === 'string' &&
      input.functionEvent.event.function.startsWith('Add')
    ) {
      const pattern = /^Add-(.+?)=>(\d+)$/;
      const match = input.functionEvent.event.function.match(pattern);
      if (!match) {
        return;
      }
      const [, idsPart, targetIdStr] = match;

      const sourceIds = idsPart
        .split('-')
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));
      const targetId = Number(targetIdStr);
      if (sourceIds.length === 0 || isNaN(targetId)) {
        return;
      }

      let total = 0;
      for (const id of sourceIds) {
        const field = fields.find((f) => f.idControl === id);
        const rawValue = field?.formControl?.value;
        if (rawValue === null || rawValue === '' || isNaN(Number(rawValue))) continue;
        total += Number(rawValue);
      }
      const targetField: InputsGeneralForm | undefined = fields.find(
        (f) => f.idControl === targetId,
      );
      if (!targetField) {
        return;
      }

      targetField.formControl?.setValue(total, { emitEvent: true });
      if (targetField && rumFuntions) {
        targetField.formControl?.markAsDirty();
      }
    }
  }

  // Copi-P=>2-454=>49 Copia valor de campo (Fisica) P=>2 o (Moral) P=>1
  public copiValue(input: InputsGeneralForm, fields: InputsGeneralForm[], rumFuntions: boolean) {
    if (
      typeof input.functionEvent?.event?.function === 'string' &&
      input.functionEvent.event.function.startsWith('Copi')
    ) {
      const pattern = /^Copi-P=>(\d+)-(\d+)=>(\d+)$/;
      const match = input.functionEvent.event.function.match(pattern);
      if (!match) {
        return;
      }
      const [, pIdStr, sourceIdStr, targetIdStr] = match;
      const pId = Number(pIdStr);
      const sourceId = Number(sourceIdStr);
      const targetId = Number(targetIdStr);

      if (pId == 1 || pId == 2) {
        const sourceField = fields.find((f) => f.idControl === sourceId);
        const targetField = fields.find((f) => f.idControl === targetId);
        if (!sourceField || !targetField) {
          return;
        }
        const valueToCopy = sourceField.formControl?.value;
        targetField.formControl?.setValue(valueToCopy, { emitEvent: true });
        if (rumFuntions) {
          targetField.formControl?.markAsDirty();
        }
      }
    }
  }

  // PostalCode-C=>26-D=>29-E=>27-Col=>25
  public funtionCopiPostalCode(
    input: InputsGeneralForm,
    fields: InputsGeneralForm[],
    rumFuntions: boolean,
  ) {
    if (
      typeof input.functionEvent?.event?.function === 'string' &&
      input.functionEvent.event.function.startsWith('PostalCode')
    ) {
      const pattern = /C=>(\d+)-D=>(\d+)-E=>(\d+)-Col=>(\d+)/;
      const match = input.functionEvent.event.function.match(pattern);
      if (match && input.formControl?.value && input.formControl?.valid) {
        const [, cId, dId, eId, colId] = match.map((v) => Number(v));
        this.getColoniesByPostalCode({
          postalCode: input.formControl.value,
        }).subscribe((result) => {
          if (!result) return;
          fields.forEach((fld) => {
            if (fld.idControl === cId && result.ciudad != '') {
              fld.formControl?.setValue(result.ciudad);
              fld.formControl?.markAsDirty();
            }
            if (fld.idControl === dId && result.delegacion != '') {
              fld.formControl?.setValue(result.delegacion);
              fld.formControl?.markAsDirty();
            }
            if (fld.idControl === eId && result.estado != '') {
              fld.formControl?.setValue(result.estado);
              fld.formControl?.markAsDirty();
            }
            if (fld.idControl === colId && fld.typeInput === 'input-general-select') {
              fld.inputSelect = { value: 'value', description: 'description' };
              fld.arrayData = result.colonies;
              fld.formControl?.markAsDirty();
            }
          });
        });
      }
    }
  }

  // Servicio para obtener colonias por codigo postal
  public getColoniesByPostalCode(body: { postalCode: string }): Observable<AddressInfo | null> {
    const params = this.generalService.buildParams(body);
    return this.http.get<AddressInfo>(`${this.URL_API}/getColoniesByPostalCode`, { params }).pipe(
      take(1),
      catchError(() => of(null)),
    );
  }

  // CopySolicitudRFC-FULL por tipo de persona
  public copySolicitudFull(input: InputsGeneralForm, quotationId: number) {
    if (input.functionEvent?.event.function?.startsWith('copySolicitudRFC-FULL')) {
      if (input.formControl?.valid) {
        this.copySolicitud({
          idCotizacionNueva: `${quotationId}`,
          rfc: input.formControl.value,
        }).subscribe((ress) => {
          if (ress) {
            if (ress.estado == 'OK') {
              this.emitFuntionInput.emit({ action: 'copySolicitud', body: {} });
              this.generalService.viewSnacAlert(
                'Campos Actualizados',
                'normal',
                'Formulario guardado',
              );
            }
          }
        });
      }
    }
  }

  // RFC con homoclave servicio
  public copySolicitud(body: {
    idCotizacionNueva: string;
    rfc: string;
  }): Observable<{ message: string; rfc: number; estado: string } | null> {
    const params = this.generalService.buildParams(body);
    return this.http
      .post<{
        message: string;
        rfc: number;
        estado: string;
      }>(this.URL_API + '/copy-solicitud/by-rfc', {}, { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  // Convierte el valor actual de un FormControl a mayúsculas
  public toUppercaseControl(control: FormControl) {
    const value = control?.value;
    if (typeof value === 'string') {
      control.setValue(value.toUpperCase(), { emitEvent: true });
      control.markAsDirty();
    }
  }
}
